import React, {useMemo, useState} from "react";
import styled, {css, keyframes} from "styled-components";
import {CraftingRecipe, GameItems} from "../State/GameData";
import {useInventory} from "../State/InventoryContext";
import {useHoldProgress} from "../Utility/useHoldProgress";
import {ItemIcon} from "../SharedComponents/ItemIcon";
import {popRise} from "../SharedComponents/Animations";

const ICON_SIZE = 44;

const blink = keyframes`
  0% { background-color: #ffffff; }
  40% { background-color: #f8fbff; }
  70% { background-color: #f0f4ff; }
  100% { background-color: #ffffff; }
`;

const S = {
    Card: styled.div<{ $highlighted: boolean; $blinking: boolean }>`
      border: 2px solid ${props => props.$highlighted ? '#0d6efd' : '#d1d5db'};
      border-radius: 12px;
      padding: 14px;
      background: white;
      min-height: 340px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      box-shadow: 0 4px 10px rgba(0,0,0,0.04);
      transition: border-color 150ms ease, box-shadow 150ms ease, background-color 150ms ease;
      ${props => props.$blinking ? css`animation: ${blink} 420ms ease;` : ""}
      position: relative;
    `,
    TitleRow: styled.div`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    `,
    ItemHeader: styled.div`
      display: flex;
      align-items: center;
      gap: 10px;
    `,
    IconPill: styled.div`
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 6px;
      background: #f3f4f6;
      border-radius: 12px;
    `,
    ItemName: styled.div`
      font-weight: 700;
      color: #0b3d91;
      font-size: 16px;
    `,
    Description: styled.p`
      margin: 0;
      color: #4b5563;
      font-size: 14px;
    `,
    QuantityTag: styled.span`
      border-radius: 999px;
      background: #0d6efd;
      color: white;
      padding: 6px 12px;
      font-weight: 700;
      font-size: 13px;
    `,
    IngredientList: styled.ul`
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
    `,
    IngredientItem: styled.li<{ $lacking: boolean; $focused: boolean }>`
      list-style: none;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      border-radius: 10px;
      border: 1px solid ${props => props.$focused ? '#0d6efd' : '#e5e7eb'};
      background: ${props => props.$lacking ? '#fff1f2' : '#f9fafb'};
      box-shadow: ${props => props.$focused ? '0 0 0 3px rgba(13,110,253,0.15)' : 'none'};
      transition: border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease;
      cursor: pointer;
      &:hover { transform: translateX(2px); }
    `,
    IngredientLabel: styled.span`
      flex: 1;
      font-size: 14px;
      color: #1f2937;
    `,
    IngredientCount: styled.span<{ $lacking: boolean }>`
      font-weight: 700;
      color: ${props => props.$lacking ? '#c1121f' : '#0b3d91'};
    `,
    Footer: styled.div`
      display: flex;
      flex-direction: column;
      gap: 8px;
    `,
    CraftButton: styled.button<{ $disabled: boolean }>`
      width: 100%;
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid #d1d5db;
      background-color: ${props => props.$disabled ? '#f5f5f5' : '#e5e7eb'};
      color: ${props => props.$disabled ? '#9ca3af' : '#111827'};
      font-weight: 600;
      cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
    `,
    ProgressShell: styled.div`
      height: 12px;
      background-color: #f3f4f6;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      overflow: hidden;
    `,
    ProgressBar: styled.div<{ $progress: number }>`
      height: 100%;
      width: ${props => props.$progress}%;
      background: linear-gradient(90deg, #ffc107, #ff8700);
      transition-property: width;
      transition-timing-function: linear;
    `,
    ResultPops: styled.div`
      position: absolute;
      top: 10px;
      right: 10px;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;
      pointer-events: none;
    `,
    ResultPop: styled.div`
      background: rgba(13, 110, 253, 0.12);
      color: #0d6efd;
      font-weight: 700;
      padding: 4px 8px;
      border-radius: 999px;
      font-size: 13px;
      animation: ${popRise} 820ms ease forwards;
      box-shadow: 0 4px 10px rgba(0,0,0,0.05);
    `,
};

type CraftingCardProps = {
    recipe: CraftingRecipe;
    isHighlighted: boolean;
    focusedItemId?: string;
    onIngredientHover: (itemId?: string) => void;
    onIngredientClick: (itemId: string) => void;
    onAddToQueue?: (recipeId: string) => void;
};

export function CraftingCard({recipe, isHighlighted, focusedItemId, onIngredientHover, onIngredientClick, onAddToQueue}: CraftingCardProps) {
    const {getItemQuantity, craftItem} = useInventory();
    const [blinking, setBlinking] = useState(false);
    const [popIndicators, setPopIndicators] = useState<number[]>([]);
    const resultItem = GameItems[recipe.result.itemId];
    const canCraft = useMemo(() => recipe.ingredients.every(ing => getItemQuantity(ing.itemId) >= ing.quantity),
        [recipe.ingredients, getItemQuantity]);

    // Only use hold progress in non-queue mode
    const holdProgressResult = useHoldProgress({
        durationMs: recipe.durationMs,
        onComplete: () => {
            craftItem(recipe.id);
            setBlinking(true);
            setTimeout(() => setBlinking(false), 360);
            const now = Date.now();
            const popIds = Array.from({length: recipe.result.quantity}, (_, idx) => now + idx);
            setPopIndicators(prev => [...prev, ...popIds]);
            popIds.forEach(id => setTimeout(() => {
                setPopIndicators(prev => prev.filter(existing => existing !== id));
            }, 820));
        },
    });

    const progress = onAddToQueue ? 0 : holdProgressResult.progress;
    const eventHandlers = onAddToQueue ? {} : holdProgressResult.eventHandlers;

    const handleAddToQueue = () => {
        if (onAddToQueue) {
            onAddToQueue(recipe.id);
            setBlinking(true);
            setTimeout(() => setBlinking(false), 420);
        }
    };

    const startHandlers = onAddToQueue
        ? {}
        : (canCraft ? eventHandlers : {
            onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
            onMouseUp: undefined,
            onMouseLeave: undefined,
        });

    return <S.Card id={`craft-card-${recipe.result.itemId}`} $highlighted={isHighlighted} $blinking={blinking}>
        <S.TitleRow>
            <S.ItemHeader>
                <S.IconPill>
                    <ItemIcon itemId={recipe.result.itemId} size={ICON_SIZE} background="transparent" title={resultItem.name}/>
                </S.IconPill>
                <div>
                    <S.ItemName>{resultItem.name}</S.ItemName>
                    {recipe.description && <S.Description>{recipe.description}</S.Description>}
                </div>
            </S.ItemHeader>
            <S.QuantityTag title="Output quantity">x{recipe.result.quantity}</S.QuantityTag>
        </S.TitleRow>

        <S.IngredientList>
            {recipe.ingredients.map(ingredient => {
                const owned = getItemQuantity(ingredient.itemId);
                const lacking = owned < ingredient.quantity;
                const ingredientName = GameItems[ingredient.itemId].name;
                return <S.IngredientItem
                    key={ingredient.itemId}
                    $lacking={lacking}
                    $focused={focusedItemId === ingredient.itemId}
                    onMouseEnter={() => onIngredientHover(ingredient.itemId)}
                    onMouseLeave={() => onIngredientHover(undefined)}
                    onClick={() => onIngredientClick(ingredient.itemId)}
                >
                    <S.IconPill>
                        <ItemIcon itemId={ingredient.itemId} size={ICON_SIZE} background="transparent" title={ingredientName}/>
                    </S.IconPill>
                    <S.IngredientLabel>{ingredientName}</S.IngredientLabel>
                    <S.IngredientCount $lacking={lacking}>{owned}/{ingredient.quantity}</S.IngredientCount>
                </S.IngredientItem>;
            })}
        </S.IngredientList>

        <S.Footer>
            <S.CraftButton
                $disabled={!canCraft}
                onClick={onAddToQueue ? handleAddToQueue : undefined}
                {...startHandlers}
            >
                {!canCraft
                    ? "Missing ingredients"
                    : onAddToQueue
                    ? "Add to crafting queue"
                    : "Hold to craft"}
            </S.CraftButton>
            {!onAddToQueue && (
                <S.ProgressShell>
                    <S.ProgressBar $progress={progress} style={{transitionDuration: `${recipe.durationMs / 10}ms`}} />
                </S.ProgressShell>
            )}
        </S.Footer>
        <S.ResultPops>
            {popIndicators.map(id => <S.ResultPop key={id}>+1</S.ResultPop>)}
        </S.ResultPops>
    </S.Card>;
}

